import Box from '@mui/material/Box';
import { type SxProps, type Theme } from '@mui/material/styles';

import {
  BRAND_PRINT_INTERVAL_MS,
  BRAND_SYMBOL_ASPECT,
  BRAND_SYMBOL_ORIGIN,
  BRAND_SYMBOL_RIDGES,
  BRAND_SYMBOL_VIEWBOX,
  signalBreathe,
  signalPrint,
  signalPrintInterval,
  signalPulse,
  getBrandRidgeDelay,
  type BrandSymbolAnimation,
} from '@/components/Loaders/utils';
import { loadingTokens } from '@/theme/themePrimitives';

interface BrandSymbolProps {
  /** Height in px; the width follows the symbol's proportion. */
  size?: number;
  /** `print` opens the app, `pulse` covers a whole-shell switch. */
  animation?: BrandSymbolAnimation;
  /**
   * Rest between B1 prints. Only `BRAND_PRINT_INTERVAL_MS` is paced; the loader cycle
   * otherwise plays straight through.
   */
  intervalMs?: number;
  /** One flat color for every ridge (a muted or failed state). The brand tones otherwise. */
  color?: string;
  sx?: SxProps<Theme>;
}

/**
 * The official symbol drawn as its six ridges, so each can be animated alone. It never
 * spins, never deforms and never changes color while animating.
 */
export function BrandSymbol({
  size = 56,
  animation = 'none',
  intervalMs,
  color,
  sx: sxProp,
}: BrandSymbolProps) {
  const paced = animation === 'print' && intervalMs === BRAND_PRINT_INTERVAL_MS;

  return (
    <Box
      component="svg"
      viewBox={BRAND_SYMBOL_VIEWBOX}
      aria-hidden
      sx={[sx.root(size, animation, paced), ...(Array.isArray(sxProp) ? sxProp : [sxProp])]}
    >
      {BRAND_SYMBOL_RIDGES.map((ridge, index) => (
        <path
          key={ridge.d.slice(0, 16)}
          d={ridge.d}
          fill={color ?? ridge.color}
          style={
            animation === 'none'
              ? undefined
              : { animationDelay: `${getBrandRidgeDelay(animation, index)}ms` }
          }
        />
      ))}
    </Box>
  );
}

const ridgeAnimation = {
  print: `${signalPrint} ${loadingTokens.brandCycle}ms ${loadingTokens.brandEasing} infinite both`,
  pulse: `${signalPulse} 1800ms ease-in-out infinite both`,
};

const pacedPrint = `${signalPrintInterval} ${loadingTokens.brandCycle + BRAND_PRINT_INTERVAL_MS}ms ${loadingTokens.brandEasing} infinite both`;

const sx = {
  root: (size: number, animation: BrandSymbolAnimation, paced: boolean) => ({
    display: 'block',
    width: Math.round(size * BRAND_SYMBOL_ASPECT),
    height: size,
    flexShrink: 0,
    overflow: 'visible',
    '& path': { transformBox: 'view-box', transformOrigin: BRAND_SYMBOL_ORIGIN },
    ...(animation !== 'none' && {
      '& path': {
        transformBox: 'view-box',
        transformOrigin: BRAND_SYMBOL_ORIGIN,
        animation: paced ? pacedPrint : ridgeAnimation[animation],
      },
      '@media (prefers-reduced-motion: reduce)': {
        animation: `${signalBreathe} ${loadingTokens.brandCycle}ms ease-in-out infinite`,
        '& path': { animation: 'none' },
      },
    }),
  }),
};
